import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { useLazyQuery, useMutation, useQuery } from "@apollo/client"
import "react-awesome-lightbox/build/style.css"
import "../Material/ListingHeader.css"
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { firestoreDb } from "../Components/firebase"
import ReactMarkdown from 'react-markdown'
import moment from 'moment-timezone'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import {
  ASSIGN_CONVERSATION,
  SUBSCRIBE_CONVERSATION,
  UNSUBSCRIBE_CONVERSATION,
  UPDATE_CONVERSATION,
} from './Mutations'
import RepAvatar from '../Material/RepAvatar'
import MessageStatus from './MessageStatus'
import './MessageStatus.css'
import { GET_CONVERSATION, GET_INBOX_MEMBERS, ASSIGNED_UNREAD_COUNT_QUERY, MESSAGE_ATTACHMENTS } from './Queries'
import ExternalIntegrations from '../Drivers/Driver/Detail/DetailsNew/Components/ExternalIntegrations'
import { debounce } from 'lodash'
import { Link } from 'react-router-dom'
import Loader from '../Material/Loader'
import MessageComposer from './SendMessage/MessageComposer'
import { UserQuery } from '../Functions/querys'
import CommsEditConversationModal from './CommsEditConversationModal'

const COMMS_MESSAGES_COLLECTION_NAME = "messaging_message"
const NOTES_COLLECTION_NAME = "messaging_conversationnote"

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'avif'])

const extractFilename = (urlOrName) => {
  if (!urlOrName) return 'Attachment'
  const source = urlOrName.split('?')[0]
  const parts = source.split('/')
  const lastPart = parts[parts.length - 1] || urlOrName
  try {
    return decodeURIComponent(lastPart)
  } catch (error) {
    return lastPart
  }
}

const extensionFromName = (name) => {
  if (!name) return ''
  const parts = name.split('.')
  if (parts.length < 2) return ''
  return parts.pop().toLowerCase()
}

const isImageAttachment = ({ mimeType, extension, name, url }) => {
  if (mimeType && mimeType.startsWith('image/')) return true
  const ext = extension || extensionFromName(name || extractFilename(url))
  return IMAGE_EXTENSIONS.has(ext)
}

const getAttachmentGlyph = (mimeTypeOrName) => {
  const value = mimeTypeOrName?.toLowerCase() || ''
  if (value.includes('pdf') || value.endsWith('.pdf')) return '📄'
  if (value.includes('sheet') || value.endsWith('.xls') || value.endsWith('.xlsx') || value.endsWith('.csv')) return '📊'
  if (value.includes('word') || value.endsWith('.doc') || value.endsWith('.docx')) return '📝'
  if (value.includes('video') || value.endsWith('.mp4') || value.endsWith('.mov')) return '🎥'
  if (value.includes('audio') || value.endsWith('.mp3') || value.endsWith('.wav')) return '🎧'
  return '📎'
}

const formatUserDisplayName = (user) => {
  if (!user) return ''
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return fullName || user.username || 'Unassigned'
}

const CommsConversation = ({ 
  conversationIdFromParent, 
  createResponseForPrompt, 
  slimView = false, 
  onConversationArchived = null,
  ...props }) => {
  const { currentUser } = useContext(CurrentUserContext)
  const [messages, setMessages] = useState([])
  const [notes, setNotes] = useState([])
  const [conversationId, setConversationId] = useState(conversationIdFromParent)
  const [actionMenuOpen, setActionMenuOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('')
  const [assignError, setAssignError] = useState(null)
  const [selectedSubscriberId, setSelectedSubscriberId] = useState('')
  const [subscriptionError, setSubscriptionError] = useState(null)
  const [subscribersMenuOpen, setSubscribersMenuOpen] = useState(false)
  const messagesContainerRef = useRef(null)
  const subscribersMenuRef = useRef(null)
  const subscribersButtonRef = useRef(null)
  const actionMenuRef = useRef(null)
  const actionButtonRef = useRef(null)

  // ✅ Track message count per-conversation to avoid cross-conversation bleed
  const prevTotalMessagesCountRef = useRef(0)

  const [updateConversationMutation, {loading: updateConversationLoading}] = useMutation(UPDATE_CONVERSATION, {
    refetchQueries: [{ query: GET_CONVERSATION, variables: { id: conversationId } }, { query: ASSIGNED_UNREAD_COUNT_QUERY }],
    onError: (error) => console.error('Update conversation error', error),
  })

  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
    refetch: refetchConversation,
  } = useQuery(GET_CONVERSATION, {
    variables: { id: conversationId },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
  })

  const conversation = conversationData?.conversation || null
  const inboxId = conversation?.channel?.inbox?.id

  const { data: inboxMembersData, loading: inboxMembersLoading } = useQuery(GET_INBOX_MEMBERS, {
    variables: { inboxId, first: 100 },
    skip: !inboxId,
    fetchPolicy: 'cache-first',
  })

  const conversationMembers = useMemo(() => {
    if (!inboxMembersData?.allInboxMembers?.edges) return []
    return inboxMembersData.allInboxMembers.edges
      .map(edge => edge?.node)
      .filter(node => node?.user)
  }, [inboxMembersData])

  const assignableMembers = useMemo(
    () => conversationMembers.filter(member => member?.canBeAssigned),
    [conversationMembers]
  )

  const isCurrentUserAssigned = useMemo(() => (
    conversation?.assignedTo &&
    currentUser &&
    conversation?.assignedTo?.username === currentUser?.username
  ), [conversation?.assignedTo?.username, currentUser?.username])

  const currentUserIdString = useMemo(() => (
    currentUser?.id ? String(currentUser.id) : null
  ), [currentUser?.id])

  const debouncedRefetch = useMemo(
    () => debounce(() => refetchConversation(), 250),
    [refetchConversation]
  )

  useEffect(() => {
    return () => { debouncedRefetch.cancel() }
  }, [debouncedRefetch])

  const baseConversationId = conversation?.id

  const [assignConversationMutation, { loading: assignLoading }] = useMutation(ASSIGN_CONVERSATION, {
    refetchQueries: [{ query: ASSIGNED_UNREAD_COUNT_QUERY }],
    onCompleted: (data) => {
      const result = data?.messagingAssignConversation
      if (result?.ok) {
        debouncedRefetch()
        setAssignError(null)
      } else if (result?.errors?.length) {
        setAssignError(result.errors.join(', '))
      } else {
        setAssignError('Unable to assign conversation')
      }
    },
    onError: (error) => {
      console.error('Assign conversation error', error)
      setAssignError(error.message || 'Error assigning conversation')
    },
  })

  const [subscribeConversationMutation, { loading: subscribeLoading }] = useMutation(SUBSCRIBE_CONVERSATION, {
    onCompleted: (data) => {
      const result = data?.messagingSubscribeConversation
      if (result?.ok) {
        debouncedRefetch()
        setSubscriptionError(null)
      } else if (result?.errors?.length) {
        setSubscriptionError(result.errors.join(', '))
      } else {
        setSubscriptionError('Unable to subscribe to conversation')
      }
    },
    onError: (error) => {
      console.error('Subscribe conversation error', error)
      setSubscriptionError(error.message || 'Error subscribing to conversation')
    },
  })

  const [unsubscribeConversationMutation, { loading: unsubscribeLoading }] = useMutation(UNSUBSCRIBE_CONVERSATION, {
    onCompleted: (data) => {
      const result = data?.messagingUnsubscribeConversation
      if (result?.ok) {
        debouncedRefetch()
        setSubscriptionError(null)
      } else if (result?.errors?.length) {
        setSubscriptionError(result.errors.join(', '))
      } else {
        setSubscriptionError('Unable to unsubscribe from conversation')
      }
    },
    onError: (error) => {
      console.error('Unsubscribe conversation error', error)
      setSubscriptionError(error.message || 'Error unsubscribing from conversation')
    },
  })

  // ✅ Keep local conversationId in sync with parent
  useEffect(() => {
    setConversationId(conversationIdFromParent)
  }, [conversationIdFromParent])

  // ✅ Reset per-conversation counters when switching conversations
  useEffect(() => {
    prevTotalMessagesCountRef.current = 0
  }, [conversationId])

  useEffect(() => {
    if (!conversationId) {
      setMessages([])
      return
    }

    const q = query(
      collection(firestoreDb, COMMS_MESSAGES_COLLECTION_NAME),
      where("conversation_graphene_id", "==", conversationId),
      orderBy("timestamp", "desc"),
      limit(100),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs
      const nextMessages = docs.map(doc => ({ id: doc.id, ...doc.data(), type: "message" }))
      setMessages(nextMessages)

      // only refetch on growth (new msg)
      if (docs.length > prevTotalMessagesCountRef.current) {
        debouncedRefetch()
      }
      prevTotalMessagesCountRef.current = docs.length
    })

    return () => unsubscribe()
  }, [conversationId])

  useEffect(() => {
    if (!conversationId) {
      setNotes([])
      return
    }

    const q = query(
      collection(firestoreDb, NOTES_COLLECTION_NAME),
      where("conversation_graphene_id", "==", conversationId),
      orderBy("date_added", "desc"),
      limit(50),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotes = []
      snapshot.forEach((doc) => {
        fetchedNotes.push({ id: doc.id, ...doc.data(), type: "note" })
      })
      setNotes(fetchedNotes)
    })

    return () => unsubscribe()
  }, [conversationId])

  const combinedFeed = useMemo(() => {
    return [...messages, ...notes].sort(
      (a, b) => new Date(a.timestamp || a.date_added) - new Date(b.timestamp || b.date_added)
    )
  }, [messages, notes])

  const [fetchAttachmentsByIds, { data: attachmentsData }] = useLazyQuery(MESSAGE_ATTACHMENTS)

  const baseAttachmentsMap = useMemo(() => {
    const map = new Map()

    combinedFeed.forEach(message => {
      if (!message?.attachments && !message?.attachment_urls) return

      if (message.attachments) {
        message.attachments?.forEach((attachment, index) => {
          const attachmentId = attachment?.graphene_id ?? `${message.graphene_id}-${index}`

          map.set(attachmentId, {
            attachmentId,
            grapheneId: attachment?.graphene_id,
            url: attachment?.url ?? attachment,
            name: attachment?.name ?? `Attachment ${index + 1}`,
            dateAdded: message.date_added,
            isImage: attachment?.extension && isImageAttachment({
              extension: attachment.extension,
              name: attachment?.name,
              url: attachment?.url
            }),
          })
        })
        return
      }

      if (message.attachment_urls) {
        message.attachment_urls?.forEach((url, index) => {
          const attachmentId = `${message.graphene_id}-${index}`
          map.set(attachmentId, {
            attachmentId,
            url,
            name: extractFilename(url),
            dateAdded: message.date_added,
            isImage: isImageAttachment({ name: extractFilename(url), url })
          })
        })
      }
    })

    return map
  }, [combinedFeed])

  const [attachmentsMap, setAttachmentsMap] = useState(baseAttachmentsMap)
  useEffect(() => {
    setAttachmentsMap(baseAttachmentsMap)
  }, [baseAttachmentsMap])

  const SIX_DAYS = 6 * 24 * 60 * 60 * 1000
  const expiredAttachmentIds = useMemo(() => {
    const ids = []

    attachmentsMap.forEach((attachment) => {
      const { url, grapheneId, dateAdded } = attachment

      const isExpiredGcsUrl =
        typeof url === "string" &&
        grapheneId &&
        url.includes("storage.googleapis.com") &&
        Date.now() - new Date(dateAdded).getTime() > SIX_DAYS

      if (isExpiredGcsUrl) ids.push(grapheneId)
    })

    return ids
  }, [attachmentsMap])

  const fetchedIdsRef = useRef(new Set())
  useEffect(() => {
    const idsToFetch = expiredAttachmentIds.filter(id => !fetchedIdsRef.current.has(id))
    if (idsToFetch.length === 0) return

    idsToFetch.forEach(id => fetchedIdsRef.current.add(id))

    fetchAttachmentsByIds({
      variables: { ids: idsToFetch },
    })
  }, [expiredAttachmentIds, fetchAttachmentsByIds])

  useEffect(() => {
    const nodes = attachmentsData?.allMessageAttachments?.edges
      ?.map(e => e?.node)
      .filter(Boolean)

    if (!nodes?.length) return

    setAttachmentsMap(prev => {
      const next = new Map(prev)

      next.forEach((value, key) => {
        if (!value.grapheneId) return

        const updated = nodes.find(n => n.id === value.grapheneId)
        if (!updated) return

        next.set(key, {
          ...value,
          url: updated.url,
        })
      })

      return next
    })
  }, [attachmentsData])

  const lastInboundSuggestedResponse = useMemo(() => {
    if (messages.length === 0) return ''
    const lastMessage = messages[0]
    if (!lastMessage?.is_inbound) return ''
    return (
      lastMessage?.ai_agent_suggested_response ||
      lastMessage?.message?.ai_agent_suggested_response ||
      ''
    )
  }, [messages])

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }, [combinedFeed.length])

  useEffect(() => {
    if (conversation?.assignedTo?.id) {
      setSelectedAssigneeId(conversation.assignedTo.id)
    } else if (!assignLoading) {
      setSelectedAssigneeId('')
    }
  }, [conversation?.assignedTo?.id, assignLoading])

  const handleAssigneeChange = async (event) => {
    const value = event.target.value
    setSelectedAssigneeId(value)
    setAssignError(null)

    if (!value || !baseConversationId) return

    try {
      await assignConversationMutation({
        variables: {
          conversationId: baseConversationId,
          userId: value,
        },
      })
    } catch (error) {
      // handled by onError
    }
  }

  // Only mark as read when a new message arrives and the conversation is currently unread
  const prevMessagesLengthRef = useRef(messages.length)
  useEffect(() => {
    // Only mark as read if:
    // - There is a conversation
    // - The current user is the assigned agent
    // - The conversation is currently unread
    // - There are new messages (messages.length increases)
    if (!baseConversationId || !currentUserIdString || !isCurrentUserAssigned) return
    if (!conversation?.isReadByAssignedAgent && messages.length > prevMessagesLengthRef.current) {
      updateConversationMutation({
        variables: { conversationId: baseConversationId, isReadByAssignedAgent: true },
        refetchQueries: [UserQuery],
      })
    }
    prevMessagesLengthRef.current = messages.length
  }, [baseConversationId, currentUserIdString, isCurrentUserAssigned, conversation?.isReadByAssignedAgent, messages.length, updateConversationMutation])

  const subscriberEdges = conversation?.subscribers?.edges || []
  const subscribers = useMemo(() => subscriberEdges.map(edge => edge?.node), [subscriberEdges])
  const subscriberIdSet = useMemo(() => new Set(subscribers.map(subscriber => subscriber.id)), [subscribers])

  const availableSubscriberOptions = useMemo(() => {
    if (!conversationMembers?.length) return []
    const seen = new Set()
    const options = []

    conversationMembers.forEach(member => {
      if (subscriberIdSet.has(member.user.id) || seen.has(member.user.id)) return
      seen.add(member.user.id)
      options.push({
        value: member.user.id,
        label: formatUserDisplayName(member.user),
      })
    })

    return options
  }, [conversationMembers, subscriberIdSet])

  useEffect(() => {
    if (selectedSubscriberId && !availableSubscriberOptions.some(option => option.value === selectedSubscriberId)) {
      setSelectedSubscriberId('')
    }
  }, [selectedSubscriberId, availableSubscriberOptions])

  useEffect(() => {
    setSelectedSubscriberId('')
    setSubscriptionError(null)
  }, [conversation?.id])

  useEffect(() => {
    setSubscribersMenuOpen(false)
    setActionMenuOpen(false)
    setEditModalOpen(false)
  }, [conversation?.id])

  useEffect(() => {
    if (!subscribersMenuOpen) return

    const handleClickOutside = (event) => {
      if (
        subscribersMenuRef.current &&
        !subscribersMenuRef.current.contains(event.target) &&
        !subscribersButtonRef.current?.contains(event.target)
      ) {
        setSubscribersMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [subscribersMenuOpen])

  useEffect(() => {
    if (!actionMenuOpen) return

    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target) &&
        !actionButtonRef.current?.contains(event.target)
      ) {
        setActionMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [actionMenuOpen])

  const subscriptionLoading = subscribeLoading || unsubscribeLoading

  const handleSubscribe = async (userId = null) => {
    if (!baseConversationId) return
    setSubscriptionError(null)
    try {
      await subscribeConversationMutation({
        variables: {
          conversationId: baseConversationId,
          userId: userId ? userId : null,
        },
      })
    } catch (error) {
      // handled in onError
    }
  }

  const handleAddSelectedSubscriber = () => {
    if (!selectedSubscriberId) return
    handleSubscribe(selectedSubscriberId)
    setSelectedSubscriberId('')
  }

  const handleSubscribeSelf = () => {
    handleSubscribe(null)
  }

  const handleUnsubscribe = async (userId) => {
    if (!baseConversationId) return
    setSubscriptionError(null)
    try {
      await unsubscribeConversationMutation({
        variables: {
          conversationId: baseConversationId,
          userId,
        },
      })
    } catch (error) {
      // handled in onError
    }
  }

  const isCurrentUserSubscribed = currentUserIdString ? subscriberIdSet.has(currentUserIdString) : false
  const canCurrentUserSubscribe = currentUserIdString
    ? conversationMembers.some(member => member.user.id === currentUserIdString)
    : false

  if (!currentUser) return <div></div>

  if (conversationError) {
    return (
      <div className="CommsSession">
        <div className="alert alert-danger m-3">
          Failed to load conversation details.
        </div>
      </div>
    )
  }

  if (!conversationId) {
    return (
      <div className="CommsSession">
        <div className="CommsSessionPlaceholder">Select a conversation to begin.</div>
      </div>
    )
  }

  if (!conversation && !conversationLoading) {
    return (
      <div className="CommsSession">
        <div className="CommsSessionPlaceholder">Conversation not found.</div>
      </div>
    )
  }

  if (!conversation && conversationLoading) return <Loader />

  const contactName = conversation?.contact_name ?? conversation?.contactName ?? ''
  const contactPhone = conversation?.contact_phone ?? conversation?.contactPhone ?? ''
  const conversationTitle = conversationLoading ? 'Loading conversation…' : (conversation?.subject || contactName || 'Conversation')
  const driverName = conversation?.driver?.name ?? conversation?.driver?.fullName ?? ''
  const driverBranchName = conversation?.driver?.branch?.name ?? ''
  const conversationChannelName = conversation?.channel?.name ?? ''
  const isArchived = conversation?.status === "CLOSED"
  const driverId = conversation?.driver?.id ? conversation.driver.id : null
  const assignedDisplayName = conversation?.assignedTo ? formatUserDisplayName(conversation.assignedTo) : 'Unassigned'
  const hasAssigneeOption = selectedAssigneeId
    ? assignableMembers.some(member => member?.user?.id === selectedAssigneeId)
    : false
  const combinedActionError = [assignError, subscriptionError].filter(Boolean).join(' • ')

  return (
    <div>
      {!slimView && <ExternalIntegrations driverId={conversation?.driver?.id || null} />}
      <div className={`CommsSession ${slimView ? "CommsSession--slim" : ""}`}>
        <div className="CommsSessionHeader">
          <div className="CommsHeaderLeft">
            <RepAvatar text={contactName || conversationTitle} />&nbsp;
            <div>
              <div className="CommsHeaderTitle">
                <Link to={`/comms/conversation/${conversation?.id}/`} className="CommsHeaderLink">
                  {conversationTitle}
                </Link>
              </div>
              <div className="CommsHeaderMeta">
                {contactPhone && <span className="CommsHeaderBadge">{contactPhone}</span>}
                {conversation?.channel?.inbox?.name && (
                  <span className="CommsHeaderBadge CommsHeaderBadge--inbox">{conversation.channel.inbox.name}</span>
                )}
                {driverName && (
                  driverId ? (
                    <a href={`/driver/${driverId}/`} className="CommsHeaderLink">{driverName}</a>
                  ) : (
                    <span className="CommsHeaderBadge">{driverName}</span>
                  )
                )}
                {driverBranchName && <span className="CommsHeaderBadge">Branch: {driverBranchName}</span>}
                {conversationChannelName && <span className="CommsHeaderBadge">Channel: {conversationChannelName}</span>}
              </div>
            </div>
          </div>

          <div className="CommsHeaderRight">
            <div className="CommsHeaderUtilities">
              <div className="CommsAssignmentControl">
                <select
                  id="conversation-assignee"
                  className="CommsAssignmentSelect"
                  value={selectedAssigneeId}
                  onChange={handleAssigneeChange}
                  disabled={assignLoading || inboxMembersLoading || assignableMembers.length === 0 || conversationLoading}
                >
                  <option value="">{assignableMembers.length === 0 ? 'No available agents' : 'Unassigned'}</option>
                  {!hasAssigneeOption && selectedAssigneeId && (
                    <option value={selectedAssigneeId}>
                      Assigned To: {assignedDisplayName}
                    </option>
                  )}
                  {assignableMembers.map(member => (
                    <option key={member.user.id} value={member.user.id}>
                      {selectedAssigneeId === member.user.id ? 'Assigned To: ' : ''}
                      {formatUserDisplayName(member.user)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="CommsHeaderActions">
              <button
                className={`CommsHeaderQuickActionButton${isArchived ? ' is-secondary' : ''}`}
                disabled={updateConversationLoading}
                onClick={() => {
                  setSubscribersMenuOpen(false)
                  setActionMenuOpen(false)
                  updateConversationMutation({
                    variables: { conversationId: conversation.id, status: isArchived ? "OPEN" : "CLOSED" }
                  }).then(() => {
                    !isArchived && onConversationArchived && typeof onConversationArchived === 'function' && onConversationArchived(conversation.id)
                  })
                    
                  
                }}
                aria-label={isArchived ? "Reopen conversation" : "Archive conversation"}
                type="button"
              >
                <i className={`fa fa-${isArchived ? 'undo' : 'archive'}`} aria-hidden="true"></i>
                <span>{isArchived ? 'Reopen' : 'Archive'}</span>
              </button>

              <div className="CommsSubscribersToggleWrapper" title="Subscribers">
                <button
                  ref={subscribersButtonRef}
                  type="button"
                  className="CommsHeaderMenuButton"
                  onClick={() => {
                    setSubscribersMenuOpen(prev => !prev)
                    setActionMenuOpen(false)
                  }}
                  aria-label="Manage subscribers"
                >
                  <i className="fa fa-users" aria-hidden="true"></i>
                </button>

                {subscribersMenuOpen && (
                  <div className="CommsSubscribersMenu" ref={subscribersMenuRef}>
                    <div className="CommsSubscribersMenuSection">
                      <div className="CommsSubscribersMenuLabel">Subscribers</div>
                      <div className="CommsSubscriberList">
                        {subscribers.length === 0 && (
                          <span className="CommsSubscriberPlaceholder">{conversationLoading ? 'Loading subscribers…' : 'No subscribers yet'}</span>
                        )}
                        {subscribers.map(subscriber => (
                          <div key={subscriber.id} className="CommsSubscriberChip">
                            <span>{formatUserDisplayName(subscriber)}</span>
                            <button
                              type="button"
                              className="CommsSubscriberRemove"
                              onClick={() => handleUnsubscribe(subscriber.id)}
                              disabled={subscriptionLoading}
                              title="Remove subscriber"
                            >
                              x
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="CommsSubscribersMenuSection">
                      <div className="CommsSubscribersMenuLabel">Add teammate</div>
                      <select
                        id="conversation-subscriber"
                        className="CommsAssignmentSelect CommsSubscriberSelect"
                        value={selectedSubscriberId}
                        onChange={(event) => setSelectedSubscriberId(event.target.value)}
                        disabled={subscriptionLoading || availableSubscriberOptions.length === 0}
                      >
                        <option value="">{availableSubscriberOptions.length === 0 ? 'No available teammates' : 'Select teammate…'}</option>
                        {availableSubscriberOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>

                      <div className="CommsSubscriberButtons">
                        <button
                          type="button"
                          className="CommsSubscriberButton"
                          onClick={handleAddSelectedSubscriber}
                          disabled={subscriptionLoading || !selectedSubscriberId}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="CommsSubscriberButton secondary"
                          onClick={handleSubscribeSelf}
                          disabled={subscriptionLoading || isCurrentUserSubscribed || !canCurrentUserSubscribe}
                          title={!canCurrentUserSubscribe ? 'You must be part of this inbox to subscribe' : undefined}
                        >
                          {isCurrentUserSubscribed ? 'Subscribed' : 'Subscribe me'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                ref={actionButtonRef}
                className="CommsHeaderMenuButton"
                onClick={() => {
                  setSubscribersMenuOpen(false)
                  setActionMenuOpen(!actionMenuOpen)
                }}
                aria-label="Conversation actions"
                type="button"
              >
                <span>⋮</span>
              </button>

              {actionMenuOpen && (
                <div className="CommsHeaderMenu" ref={actionMenuRef}>
                  <ul>
                    {isCurrentUserAssigned && (
                      <li
                        onClick={() => {
                          updateConversationMutation({ variables: { conversationId: conversation.id, isReadByAssignedAgent: false } })
                          setActionMenuOpen(false)
                        }}
                        role="button"
                      >
                        Mark as Unread
                      </li>
                    )}
                    <li
                      onClick={() => {
                        setEditModalOpen(true)
                        setActionMenuOpen(false)
                      }}
                      role="button"
                    >
                      Edit Conversation
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {combinedActionError && (
          <div className="alert alert-warning" style={{ margin: '12px 0' }}>
            {combinedActionError}
          </div>
        )}

        <div className="CommsSessionBody">
          <div id="CommsSessionMessages" className="CommsSessionMessages" ref={messagesContainerRef}>
            {combinedFeed.map((item, idx) => {
              if (item.type === "note") {
                return (
                  <div
                    key={`note-${item.id}`}
                    className="CommsNote mb-2"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      textAlign: "center",
                      margin: "0 auto",
                      fontStyle: "italic",
                      color: "#666",
                      fontSize: 13,
                      background: "#f9f9f9",
                      borderRadius: 8,
                      padding: "6px 12px",
                      width: "fit-content",
                    }}
                    title={moment(item.date_added).format("LLLL")}
                  >
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => {
                          const isMention = href && !href.startsWith("http")
                          if (isMention) return <span className="mention">{children}</span>
                          return <a href={href}>{children}</a>
                        }
                      }}
                    >
                      {item.note}
                    </ReactMarkdown>
                    <span style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>
                      — {item.created_by_username}, {moment(item.date_added).fromNow()}
                    </span>
                  </div>
                )
              }

              const attachments = item.attachments ?? item?.attachment_urls ?? []
              const messageBody = item?.message?.body ?? item.body ?? ''
              const messageBodyHtml = item?.message?.body_html ?? item.body_html ?? ''
              const statusDescription = item?.status_description || ''
              const messageDate = item?.scheduled_for_timestamp || item?.timestamp || null
              const topic = item?.topic_name || null
              const messageSubject = item?.message_subject || ''

              return (
                <div
                  key={`msg-${item.id}-${idx}`}
                  className={`CommsMessage ${item.is_inbound ? "CommsMessageUser" : "CommsMessageCustomer"}`}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                    marginBottom: 12,
                    padding: '12px 18px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    position: 'relative'
                  }}
                >
                  <RepAvatar text={item.is_inbound ? item.driver_name : item.created_by_username} repId={item.created_by_graphene_id} />&nbsp;

                  <section style={{ flex: 1, overflow: 'auto', width: '100%' }}>
                    <header
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 15 }}>{item.sender}</div>
                        {item.recipient && (
                          <div style={{ fontSize: 13, color: '#888' }}>To: {item.recipient}</div>
                        )}
                      </div>

                      {topic && (
                        <span
                          style={{
                            backgroundColor: '#eef6ff',
                            color: '#0366d6',
                            fontSize: 12,
                            fontWeight: 500,
                            padding: '2px 6px',
                            borderRadius: 4,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {topic}
                        </span>
                      )}

                      <div
                        style={{
                          fontSize: 12,
                          color: '#888',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap',
                        }}
                        title={messageDate ? moment(messageDate).format('LLLL') : ''}
                      >
                        {messageDate ? moment(messageDate).fromNow() : null}
                        <MessageStatus
                          status={item.status}
                          isInbound={item.is_inbound}
                          statusDescription={statusDescription}
                        />
                      </div>
                    </header>

                    <div>
                      <div style={{ marginTop: 4 }}>
                        {/* Dangerouslly set html */}
                        {messageSubject && 
                          <div
                            style={{
                              margin: '8px 0px',
                              borderRadius: 4,
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            Subject: {messageSubject}
                          </div>
                        }
                        {messageBodyHtml ? 
                          <div dangerouslySetInnerHTML={{ __html: messageBodyHtml }} />
                          :
                          messageBody ?
                        (<ReactMarkdown>{messageBody.replaceAll("\n", "<br />")}</ReactMarkdown>) 
                        : null
                        }
                      </div>

                      {attachments.length > 0 && (
                        <div className="CommsMessageAttachments">
                          {attachments.map((attachment, attIndex) => {
                            const attachmentId = attachment?.graphene_id ?? `${item.graphene_id}-${attIndex}`
                            const resolved = attachmentsMap.get(attachmentId)
                            if (!resolved) return null

                            return (
                              <a
                                key={resolved.attachmentId}
                                href={resolved.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`CommsMessageAttachmentCard${resolved.isImage ? ' is-image' : ''}`}
                              >
                                {resolved.isImage ? (
                                  <>
                                    <div className="CommsMessageAttachmentImage">
                                      <img src={resolved.url} alt={resolved.name} />
                                    </div>
                                    <div className="CommsMessageAttachmentCaption">
                                      <span className="CommsMessageAttachmentName">{resolved.name}</span>
                                    </div>
                                  </>
                                ) : (
                                  <div className="CommsMessageAttachmentMeta">
                                    <div className="CommsMessageAttachmentIcon">
                                      {getAttachmentGlyph(resolved.extension || resolved.name)}
                                    </div>
                                    <div className="CommsMessageAttachmentText">
                                      <span className="CommsMessageAttachmentName">{resolved.name}</span>
                                    </div>
                                  </div>
                                )}
                              </a>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              )
            })}
          </div>
        </div>

        <MessageComposer conversationId={conversationId} suggestedResponse={lastInboundSuggestedResponse} />
        {conversation && (
          <CommsEditConversationModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            conversation={conversation}
            conversationMembers={conversationMembers}
            assignableMembers={assignableMembers}
            onSave={async (input) => {
              const variables = { conversationId: conversation.id, ...input }
              const result = await updateConversationMutation({ variables })
              const ok = result?.data?.messagingUpdateConversation?.ok
              if (ok === false) {
                const errors = result?.data?.messagingUpdateConversation?.errors || []
                const message = Array.isArray(errors) && errors.length ? errors.join(', ') : 'Failed to update conversation'
                throw new Error(message)
              }
              setEditModalOpen(false)
              debouncedRefetch()
              return result
            }}
          />
        )}
      </div>
    </div>
  )
}

export default withApolloClient(CommsConversation)
